import { FC, useEffect, useState } from "react";
import {
  Button,
  Center,
  Drawer,
  Flex,
  Grid,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  AppConfigScope,
  AppConfigStatus,
  CreateAppConfigRequest,
  RemoteConfig,
  UpdateAppConfigRequest,
} from "../models/RemoteConfigs";
import { fetchWithAuthentication } from "../utilities/FetchUtil";
import { useDisclosure } from "@mantine/hooks";
import { convertToLocalDateTime } from "../utilities/DateUtil";

const RemoteConfigs: FC = () => {
  const [appNames, setAppNames] = useState([]);
  const [createIsLoading, setCreateIsLoading] = useState(false);
  const [listIsLoading, setListIsLoading] = useState(false);
  const [createText, setCreateText] = useState("");
  const [editText, setEditText] = useState("");
  const [createTextColor, setCreateTextColor] = useState("green");
  const [editTextColor, setEditTextColor] = useState("green");
  const [configsList, setConfigsList] = useState<RemoteConfig[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedConfig, setSelectedConfig] = useState<RemoteConfig>({
    name: "",
    appName: "",
    value: "",
    status: AppConfigStatus.ACTIVE,
    createdAt: new Date(),
    updatedAt: new Date(),
  });
  const [editIsLoading, setEditIsLoading] = useState(false);

  const [editDrawerOpened, editDrawerHandlers] = useDisclosure(false);
  const [createDrawerOpened, createDrawerHandlers] = useDisclosure(false);

  const createConfigForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      appName: "",
      value: "",
      scope: AppConfigScope.SERVER_ONLY,
    },
    validate: {
      value: (value: string) =>
        value.trim().length !== 0 ? null : "Value cannot be empty.",
      name: (name: string) =>
        name.trim().length !== 0 ? null : "Name cannot be empty.",
    },
  });

  const filterForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      appName: "",
      status: AppConfigStatus.ACTIVE,
    },
  });

  const editForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      value: selectedConfig.value,
      status: selectedConfig.status,
    },
    validate: {
      value: (value: string) =>
        value.trim().length !== 0 ? null : "Value cannot be empty.",
    },
  });

  const fetchAppNames = async () => {
    const response = await fetchWithAuthentication("/apps", "GET", null);
    if (response.ok) {
      const json = await response.json();
      setAppNames(json.appNames);
    } else {
      console.error("Error occurred while trying to fetch app names");
    }
  };

  const handleSubmitCreate = async () => {
    setCreateIsLoading(true);
    const request: CreateAppConfigRequest = {
      name: createConfigForm.getValues().name,
      value: createConfigForm.getValues().value,
      appName: createConfigForm.getValues().appName,
      scope: createConfigForm.getValues().scope,
    };
    const response = await fetchWithAuthentication(
      "/app_configs",
      "POST",
      request,
    );
    if (response.ok) {
      setCreateTextColor("green");
      setCreateText("New remote config successfully created.");
      setCreateIsLoading(false);
      getRemoteConfigs();
    } else if (response.status === 409) {
      setCreateTextColor("red");
      setCreateText(
        `There already exists a config with name '${createConfigForm.getValues().name}' in ${createConfigForm.getValues().appName}.`,
      );
      setCreateIsLoading(false);
    } else {
      setCreateTextColor("red");
      setCreateText(
        "Something went wrong while attempting to create new remote config.",
      );
      setCreateIsLoading(false);
    }
    createConfigForm.reset();
  };

  const handleSubmitEdit = async () => {
    setEditIsLoading(true);
    const request: UpdateAppConfigRequest = {
      name: selectedConfig.name,
      appName: selectedConfig.appName,
      value: editForm.getValues().value,
      status: editForm.getValues().status,
    };
    const response = await fetchWithAuthentication(
      "/app_configs",
      "PUT",
      request,
    );
    if (response.ok) {
      setEditTextColor("green");
      setEditText("Remote config successfully updated.");
      setEditIsLoading(false);
      getRemoteConfigs();
    } else {
      setEditTextColor("red");
      setEditText("An issue occurred while trying to update remote config.");
      setEditIsLoading(false);
    }
  };

  const getRemoteConfigs = async () => {
    setListIsLoading(true);
    const appName = filterForm.getValues().appName;
    const name = filterForm.getValues().name;
    const status = filterForm.getValues().status;
    const fetchUrl = `/app_configs?${appName ? `appName=${appName}` : ""}${name ? `&name=${name}` : ""}${status ? `&status=${status}` : ""}&pageNum=${pageNum}`;
    const response = await fetchWithAuthentication(fetchUrl, "GET", null);
    if (response.ok) {
      const json = await response.json();
      setConfigsList(json.appConfigs);
      setTotalPages(json.pageTotal);
      setListIsLoading(false);
    } else {
      console.log("Something went wrong fetching configs");
      setConfigsList([]);
      setListIsLoading(false);
    }
  };

  const statusOptions = Object.keys(AppConfigStatus).map((value) => value);

  useEffect(() => {
    getRemoteConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum]);

  useEffect(() => {
    fetchAppNames();
  }, []);

  return (
    <>
      <Drawer
        offset={8}
        radius="md"
        position="right"
        opened={editDrawerOpened}
        onClose={() => {
          editDrawerHandlers.close();
          setEditText("");
        }}
        title="Update remote config"
      >
        <form onSubmit={editForm.onSubmit(handleSubmitEdit)}>
          <Stack w={400}>
            <TextInput
              label="App name"
              value={selectedConfig.appName}
              disabled
            />
            <TextInput
              label="Config name"
              value={selectedConfig.name}
              disabled
            />
            <TextInput
              label="Value"
              {...editForm.getInputProps("value")}
              key={editForm.key("value")}
              placeholder="The value of the config"
            />
            <Select
              label="Status"
              {...editForm.getInputProps("status")}
              key={editForm.key("status")}
              placeholder="Status of config"
              data={statusOptions}
            />
            <Button type="submit">Update</Button>
            <Center mih={50}>
              {editIsLoading ? (
                <Loader />
              ) : (
                <Text c={editTextColor}>{editText}</Text>
              )}
            </Center>
          </Stack>
        </form>
      </Drawer>

      <Drawer
        offset={8}
        radius="md"
        position="left"
        opened={createDrawerOpened}
        onClose={() => {
          createDrawerHandlers.close();
          setCreateText("");
        }}
        title="Create remote config"
      >
        <form onSubmit={createConfigForm.onSubmit(handleSubmitCreate)}>
          <Stack w={400}>
            <Select
              label="App name"
              {...createConfigForm.getInputProps("appName")}
              key={createConfigForm.key("appName")}
              placeholder="Which app this config applies to"
              data={appNames}
            />
            <TextInput
              label="Config name"
              {...createConfigForm.getInputProps("name")}
              key={createConfigForm.key("name")}
              placeholder="The name for the remote config"
            />
            <TextInput
              label="Value"
              {...createConfigForm.getInputProps("value")}
              key={createConfigForm.key("value")}
              placeholder="The value of the config"
            />
            <Select
              label="Config scope"
              {...createConfigForm.getInputProps("scope")}
              key={createConfigForm.key("scope")}
              data={Object.keys(AppConfigScope)}
            />
            <Button type="submit">Submit</Button>
            <Center mih={50}>
              {createIsLoading ? (
                <Loader />
              ) : (
                <Text c={createTextColor}>{createText}</Text>
              )}
            </Center>
          </Stack>
        </form>
      </Drawer>

      <Stack justify="center" w="100%">
        <Paper p="lg" shadow="lg" radius="md" w="100%">
          <Flex direction="column" gap="lg" align="center" w="100%">
            <Title order={3}>Remote configs</Title>
            <Stack align="center">
              <Title order={4}>Search for existing configs</Title>
              <form onSubmit={filterForm.onSubmit(getRemoteConfigs)}>
                <Stack w={400}>
                  <Select
                    label="App name"
                    {...filterForm.getInputProps("appName")}
                    key={filterForm.key("appName")}
                    placeholder="Which app this config applies to"
                    data={appNames}
                  />
                  <TextInput
                    label="Config name"
                    {...filterForm.getInputProps("name")}
                    key={filterForm.key("name")}
                    placeholder="The name for the remote config"
                  />
                  <Select
                    label="Status"
                    {...filterForm.getInputProps("status")}
                    key={filterForm.key("status")}
                    placeholder="Status of config"
                    data={statusOptions}
                  />
                  <Button type="submit">Filter</Button>
                </Stack>
              </form>
              <Button mt="lg" w="100%" onClick={createDrawerHandlers.open}>
                Create new config
              </Button>
            </Stack>
          </Flex>
        </Paper>
        <Paper p="md" shadow="lg" radius="md">
          <Stack>
            <Center>
              <Title me="xl" order={5}>
                Page
              </Title>
              <Pagination
                value={pageNum}
                onChange={(value) => setPageNum(value)}
                total={totalPages}
                withEdges
              />
            </Center>
            <Grid>
              <Grid.Col span={2} fw={700}>
                App name
              </Grid.Col>
              <Grid.Col span={2} fw={700}>
                Config name
              </Grid.Col>
              <Grid.Col span={2} fw={700}>
                Value
              </Grid.Col>
              <Grid.Col span={1} fw={700}>
                Status
              </Grid.Col>
              <Grid.Col span={2} fw={700}>
                Created
              </Grid.Col>
              <Grid.Col span={2} fw={700}>
                Updated
              </Grid.Col>
              <Grid.Col span={1} fw={700}>
                Edit
              </Grid.Col>
            </Grid>
          </Stack>
        </Paper>
        <Stack>
          {listIsLoading && (
            <Center>
              <Loader />
            </Center>
          )}
          {configsList.length === 0 && (
            <Center>
              <Text>No remote configs found with current search filters.</Text>
            </Center>
          )}
          {configsList.map((config) => (
            <Paper key={config.name} p="md" shadow="lg" radius="md">
              <Grid align="center">
                <Grid.Col span={2}>{config.appName}</Grid.Col>
                <Grid.Col span={2}>{config.name}</Grid.Col>
                <Grid.Col span={2}>
                  <Text lineClamp={3}>{config.value}</Text>
                </Grid.Col>
                <Grid.Col span={1}>{config.status}</Grid.Col>
                <Grid.Col span={2}>
                  {convertToLocalDateTime(config.createdAt.toString())}
                </Grid.Col>
                <Grid.Col span={2}>
                  {convertToLocalDateTime(config.updatedAt.toString())}
                </Grid.Col>
                <Grid.Col span={1}>
                  <Button
                    onClick={() => {
                      setSelectedConfig(config);
                      editForm.reset();
                      editDrawerHandlers.open();
                    }}
                  >
                    Edit
                  </Button>
                </Grid.Col>
              </Grid>
            </Paper>
          ))}
        </Stack>
      </Stack>
    </>
  );
};

export default RemoteConfigs;
